import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStore,
    faArrowLeft,
    faArrowRight,
    faInfoCircle,
    faCheckCircle,
    faChartLine
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function AnalyticsTiendas() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Estadísticas de Tiendas
                    </h1>
                    <p className="text-xl text-gray-600">
                        Métricas específicas de rendimiento por tienda para comparar y optimizar cada ubicación.
                    </p>
                </div>

                {/* Acceso */}
                <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-3 flex items-center">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-blue-600" />
                        ¿Cómo acceder?
                    </h2>
                    <p className="text-gray-700 mb-2">
                        Para acceder a las Estadísticas de Tiendas:
                    </p>
                    <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                        <li>Inicia sesión en tu cuenta de PetitPrime</li>
                        <li>En el menú principal, selecciona <strong>"Analytics"</strong></li>
                        <li>Haz clic en <strong>"Estadísticas de Tiendas"</strong></li>
                        <li>O accede directamente a: <code className="bg-white px-2 py-1 rounded">/store-statistics/tienda/[ID_TIENDA]</code></li>
                    </ol>
                    <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                        <p className="text-sm text-gray-700">
                            <strong>Nota:</strong> Si no especificas un ID de tienda en la URL, se seleccionará automáticamente tu primera tienda disponible.
                        </p>
                    </div>
                </div>

                {/* Selector de Tienda */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        1. Seleccionar Tienda
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            En la parte superior de la página encontrarás un selector desplegable con todas tus tiendas disponibles.
                        </p>
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                <strong>¿Qué hace?</strong>
                            </p>
                            <p className="text-sm text-gray-700">
                                Permite cambiar entre diferentes tiendas para analizar el rendimiento de cada una por separado. Si eres franquiciado, solo verás las tiendas asignadas a tu cuenta.
                            </p>
                        </div>
                        <div className="flex items-start space-x-2 text-sm text-gray-600">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mt-1" />
                            <p>
                                <strong>Tip:</strong> Usa este selector para comparar el rendimiento entre diferentes ubicaciones y identificar mejores prácticas.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Métricas Principales */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        2. Métricas Principales
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Cada tienda muestra las siguientes métricas clave:
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faStore} className="mr-2 text-blue-600" />
                                    Clientes
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Número total de clientes únicos que han realizado al menos una compra en esta tienda.
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Entender el alcance de tu tienda y el tamaño de tu base de clientes local.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-green-600" />
                                    Ventas Totales
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Suma total de todas las ventas realizadas en esta tienda (en euros).
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Evaluar el rendimiento financiero de la tienda.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-purple-600" />
                                    Tasa de Conversión
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Porcentaje de clientes de la marca que han realizado al menos una compra en esta tienda específica.
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Medir la efectividad de la tienda en convertir clientes potenciales en compradores.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-orange-600" />
                                    Valor Promedio de Transacción
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Promedio del valor monetario de cada transacción realizada en la tienda (en euros).
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Identificar oportunidades de upselling y evaluar la efectividad de estrategias de venta.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-red-600" />
                                    Tasa de Redención de Puntos
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Porcentaje de puntos acumulados que han sido canjeados por premios en esta tienda.
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Evaluar la efectividad del programa de fidelización en esta ubicación.
                                </p>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-lg">
                                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                    <FontAwesomeIcon icon={faChartLine} className="mr-2 text-indigo-600" />
                                    Total Transacciones
                                </h3>
                                <p className="text-sm text-gray-600 mb-2">
                                    Número total de transacciones realizadas en la tienda.
                                </p>
                                <p className="text-xs text-gray-500">
                                    <strong>Útil para:</strong> Medir el volumen de actividad y la frecuencia de compras.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Información Adicional */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        3. Información Adicional
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Además de las métricas principales, también verás información sobre:
                        </p>

                        {/* Puntos */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Puntos Acumulados y Canjeados
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Desglose detallado de:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Puntos Acumulados:</strong> Total de puntos otorgados en esta tienda</li>
                                    <li><strong>Puntos Canjeados:</strong> Total de puntos redimidos por premios</li>
                                    <li><strong>Distribución:</strong> Visualización de la relación entre puntos acumulados y canjeados</li>
                                </ul>
                            </div>
                        </div>

                        {/* Frecuencia de Visitas */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Frecuencia de Visitas
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Métricas de frecuencia calculadas desde las fechas de visitas:
                                </p>
                                <ul className="list-disc list-inside text-sm text-gray-600 ml-4 space-y-1">
                                    <li><strong>Diaria:</strong> Visitas en las últimas 24 horas</li>
                                    <li><strong>Semanal:</strong> Visitas en los últimos 7 días</li>
                                    <li><strong>Mensual:</strong> Visitas en los últimos 30 días</li>
                                </ul>
                                <p className="text-sm text-gray-600 mt-2">
                                    Estas métricas te ayudan a entender los patrones de visita de tus clientes en esta ubicación específica.
                                </p>
                            </div>
                        </div>

                        {/* Productividad */}
                        <div className="mb-6">
                            <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                Productividad de Trabajadores
                            </h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 mb-2">
                                    Métrica calculada como el promedio de ventas por transacción:
                                </p>
                                <p className="text-sm text-gray-600">
                                    <strong>Fórmula:</strong> Ventas Totales ÷ Total Transacciones
                                </p>
                                <p className="text-sm text-gray-600 mt-2">
                                    Esta métrica te ayuda a evaluar la eficiencia operativa de la tienda y puede indicar oportunidades de mejora en el servicio al cliente.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comparación entre Tiendas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        4. Comparación entre Tiendas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para comparar el rendimiento entre diferentes tiendas:
                        </p>
                        <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
                            <li>Anota las métricas clave de una tienda (ventas totales, tasa de conversión, etc.)</li>
                            <li>Usa el selector para cambiar a otra tienda</li>
                            <li>Compara las métricas para identificar diferencias</li>
                            <li>Identifica mejores prácticas de las tiendas con mejor rendimiento</li>
                        </ol>
                        <div className="mt-4 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                            <p className="text-sm text-gray-700">
                                <strong>💡 Tip:</strong> Presta especial atención a la tasa de conversión y el valor promedio de transacción. Estas métricas pueden revelar diferencias en la efectividad del equipo o en la ubicación.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Interpretación de Datos */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        5. Interpretación de los Datos
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <p className="text-gray-700 mb-4">
                            Para aprovechar al máximo las Estadísticas de Tiendas:
                        </p>
                        <ul className="space-y-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Tasa de Conversión Alta:</strong> Indica que la tienda está haciendo un buen trabajo convirtiendo clientes potenciales. Si es baja, considera mejorar la experiencia del cliente o el entrenamiento del personal.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Valor Promedio de Transacción:</strong> Un valor alto puede indicar estrategias de upselling exitosas. Si es bajo, considera ofrecer productos complementarios o paquetes.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Tasa de Redención de Puntos:</strong> Una tasa alta indica que los clientes están activamente usando el programa de fidelización. Si es baja, considera mejorar la comunicación sobre los premios disponibles.
                                </div>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 mt-1" />
                                <div>
                                    <strong className="text-gray-800">Frecuencia de Visitas:</strong> Una frecuencia alta indica clientes satisfechos y leales. Si es baja, considera campañas de reactivación o mejoras en el servicio.
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Troubleshooting */}
                <div className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                        6. Solución de Problemas
                    </h2>
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No se muestran datos de la tienda</h4>
                                <p className="text-gray-700 text-sm">
                                    Asegúrate de que la tienda tenga transacciones registradas. Las estadísticas se calculan desde los tickets de compra. Si acabas de crear la tienda, es normal que las métricas estén en cero hasta que haya ventas.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">Las métricas parecen incorrectas</h4>
                                <p className="text-gray-700 text-sm">
                                    Verifica que los tickets estén correctamente asociados a la tienda. Las estadísticas se calculan filtrando los tickets por la tienda específica en el campo `historialConsumiciones.tienda`.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold text-gray-800 mb-2">No puedo cambiar de tienda</h4>
                                <p className="text-gray-700 text-sm">
                                    Si eres franquiciado, solo verás las tiendas asignadas a tu cuenta. Si eres propietario de marca, deberías ver todas las tiendas de tu marca. Si no ves el selector, verifica tus permisos de acceso.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-8 border-t">
                    <Link
                        to="/documentation/analytics/clientes"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Anterior: Estadísticas de Clientes
                    </Link>
                    <Link
                        to="/documentation/analytics/ofertas"
                        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                    >
                        Siguiente: Métricas de Ofertas
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default AnalyticsTiendas;

